import { useState, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { X, Server, Globe, Key, Users, Upload, Download, Filter } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { TextArea } from '@/components/ui/TextArea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { edgeTunnelApi } from '@/lib/api'
import { toast } from 'react-hot-toast'

interface EdgeTunnelGroupFormProps {
  group?: any
  onSuccess: () => void
  onCancel: () => void
}

interface ParsedIP {
  ip: string
  port: string
  region?: string
  description?: string
  latency?: string
  raw: string
}

export default function EdgeTunnelGroupFormEnhanced({ group, onSuccess, onCancel }: EdgeTunnelGroupFormProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    api_endpoint: '',
    api_key: '',
    max_users: 100,
    ip_list: '',
    user_level: 1, // 1: 所有用户, 2: 标准用户, 3: VIP专享
  })
  
  const [showParsedPreview, setShowParsedPreview] = useState(false)
  const [parsedIPs, setParsedIPs] = useState<ParsedIP[]>([])

  // 获取当前服务组的节点列表（仅编辑模式）
  const { data: groupNodesData } = useQuery({
    queryKey: ['edgetunnel-group-nodes', group?.id],
    queryFn: () => edgeTunnelApi.getNodes({ group_id: group.id }),
    enabled: !!group?.id, // 只有在编辑模式下才获取
  })

  // 地区代码转中文映射
  const regionMap: Record<string, string> = {
    'JP': '日本',
    'US': '美国',
    'DE': '德国',
    'NL': '荷兰',
    'GB': '英国',
    'UK': '英国',
    'FR': '法国',
    'CA': '加拿大',
    'AU': '澳大利亚',
    'SG': '新加坡',
    'HK': '香港',
    'TW': '台湾',
    'KR': '韩国',
    'IN': '印度',
    'RU': '俄罗斯',
    'BR': '巴西',
    'IT': '意大利',
    'ES': '西班牙',
    'SE': '瑞典',
    'NO': '挪威',
    'FI': '芬兰',
    'DK': '丹麦',
    'CH': '瑞士',
    'AT': '奥地利',
    'BE': '比利时',
    'IE': '爱尔兰',
    'PT': '葡萄牙',
    'PL': '波兰',
    'CZ': '捷克',
    'HU': '匈牙利',
    'GR': '希腊',
    'TR': '土耳其',
    'IL': '以色列',
    'AE': '阿联酋',
    'SA': '沙特阿拉伯',
    'TH': '泰国',
    'MY': '马来西亚',
    'ID': '印尼',
    'VN': '越南',
    'PH': '菲律宾',
    'NZ': '新西兰',
    'ZA': '南非',
    'EG': '埃及',
    'AR': '阿根廷',
    'CL': '智利',
    'MX': '墨西哥',
    'CO': '哥伦比亚'
  }

  // 获取中文地区名称
  const getRegionName = (code: string): string => {
    return regionMap[code.toUpperCase()] || code
  }

  // 智能解析IP函数
  const parseIPList = (ipText: string): ParsedIP[] => {
    if (!ipText.trim()) return []
    
    const lines = ipText.trim().split('\n').filter(line => line.trim())
    const parsed: ParsedIP[] = []
    
    lines.forEach(line => {
      const trimmedLine = line.trim()
      if (!trimmedLine) return
      
      // 完整格式: 172.64.229.168:2083#JP 官方优选 77ms
      const fullFormatMatch = trimmedLine.match(/^(\d+\.\d+\.\d+\.\d+):(\d+)#([A-Z]{2})\s+(.+?)\s+(\d+ms)$/i)
      if (fullFormatMatch) {
        parsed.push({
          ip: fullFormatMatch[1],
          port: fullFormatMatch[2],
          region: getRegionName(fullFormatMatch[3]),
          description: fullFormatMatch[4],
          latency: fullFormatMatch[5],
          raw: trimmedLine
        })
        return
      }
      
      // 带端口和地区: 172.64.229.168:2083#JP
      const portRegionMatch = trimmedLine.match(/^(\d+\.\d+\.\d+\.\d+):(\d+)#([A-Z]{2})$/i)
      if (portRegionMatch) {
        parsed.push({
          ip: portRegionMatch[1],
          port: portRegionMatch[2],
          region: getRegionName(portRegionMatch[3]),
          raw: trimmedLine
        })
        return
      }
      
      // 带端口: 172.64.229.168:2083
      const portMatch = trimmedLine.match(/^(\d+\.\d+\.\d+\.\d+):(\d+)$/)
      if (portMatch) {
        parsed.push({
          ip: portMatch[1],
          port: portMatch[2],
          raw: trimmedLine
        })
        return
      }
      
      // 简单IP: 172.64.229.168
      const ipMatch = trimmedLine.match(/^(\d+\.\d+\.\d+\.\d+)$/)
      if (ipMatch) {
        parsed.push({
          ip: ipMatch[1],
          port: '2083', // 默认端口
          raw: trimmedLine
        })
        return
      }
      
      // 如果都不匹配，尝试提取IP
      const anyIpMatch = trimmedLine.match(/(\d+\.\d+\.\d+\.\d+)/)
      if (anyIpMatch) {
        parsed.push({
          ip: anyIpMatch[1],
          port: '2083',
          raw: trimmedLine
        })
      }
    })
    
    return parsed
  }

  // 当IP列表改变时，自动解析
  useEffect(() => {
    const parsed = parseIPList(formData.ip_list)
    setParsedIPs(parsed)
  }, [formData.ip_list])

  // 获取地区统计
  const getRegionStats = () => {
    const stats: Record<string, number> = {}
    parsedIPs.forEach(ip => {
      if (ip.region) {
        stats[ip.region] = (stats[ip.region] || 0) + 1
      } else {
        stats['未知'] = (stats['未知'] || 0) + 1
      }
    })
    return stats
  }

  // 去重功能
  const removeDuplicateIPs = () => {
    const seen = new Set()
    const unique = parsedIPs.filter(ip => {
      const key = `${ip.ip}:${ip.port}`
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
    
    const uniqueText = unique.map(ip => ip.raw).join('\n')
    handleInputChange('ip_list', uniqueText)
    toast.success(`去重完成，从 ${parsedIPs.length} 个节点中保留了 ${unique.length} 个唯一节点`)
  }

  useEffect(() => {
    if (group && groupNodesData) {
      // 获取当前节点的IP列表
      const currentNodes = groupNodesData?.data?.data?.data?.nodes || []
      
      // 设置默认用户等级（使用第一个节点的等级，或默认为1）
      const defaultUserLevel = currentNodes[0]?.user_level || 1
      
      // 根据用户等级过滤节点IP
      const filteredNodes = currentNodes.filter((node: any) => node.user_level === defaultUserLevel)
      const currentIPs = filteredNodes.map((node: any) => node.ip_address).join('\n')
      
      setFormData({
        name: group.name || '',
        description: group.description || '',
        api_endpoint: group.api_endpoint || '',
        api_key: group.api_key || '',
        max_users: group.max_users || 100,
        ip_list: currentIPs, // 显示对应等级的节点IP
        user_level: defaultUserLevel, // 使用第一个节点的等级
      })
    }
  }, [group, groupNodesData])

  // 当用户等级改变时，更新IP列表显示
  useEffect(() => {
    if (group && groupNodesData && formData.user_level) {
      const currentNodes = groupNodesData?.data?.data?.data?.nodes || []
      // 根据选中的用户等级过滤节点
      const filteredNodes = currentNodes.filter((node: any) => node.user_level === formData.user_level)
      const filteredIPs = filteredNodes.map((node: any) => node.ip_address).join('\n')
      
      // 只更新IP列表，不更新其他字段
      setFormData(prev => ({
        ...prev,
        ip_list: filteredIPs
      }))
    }
  }, [formData.user_level, group, groupNodesData])

  // 创建服务组
  const createMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.createGroup(data),
    onSuccess: () => {
      toast.success('EdgeTunnel服务组创建成功')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '创建失败')
    },
  })

  // 更新服务组
  const updateMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.updateGroup(group.id, data),
    onSuccess: () => {
      toast.success('EdgeTunnel服务组更新成功')
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '更新失败')
    },
  })

  // 添加节点
  const addNodesMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.addNodes(data),
    onSuccess: () => {
      toast.success('节点添加成功')
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '添加节点失败')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name || !formData.api_endpoint || !formData.api_key) {
      toast.error('请填写必填字段')
      return
    }

    if (group) {
      // 编辑模式：先更新服务组基本信息，然后处理节点（如果有）
      const { ip_list, user_level, ...updateData } = formData
      
      // 总是先更新服务组基本信息
      updateMutation.mutate(updateData)
      
      // 如果有IP列表，同时添加节点
      if (ip_list.trim()) {
        addNodesMutation.mutate({
          group_id: group.id,
          ip_list: ip_list,
          user_level: user_level
        })
      }
    } else {
      // 创建服务组
      createMutation.mutate(formData)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const getUserLevelText = (level: number) => {
    switch (level) {
      case 1: return '所有用户'
      case 2: return '标准用户'  
      case 3: return 'VIP专享'
      default: return '所有用户'
    }
  }

  const getUserLevelColor = (level: number) => {
    switch (level) {
      case 1: return 'bg-green-100 text-green-800'
      case 2: return 'bg-blue-100 text-blue-800'
      case 3: return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取当前用户等级的节点统计
  const getCurrentLevelNodeCount = () => {
    if (!group || !groupNodesData) return 0
    const currentNodes = groupNodesData?.data?.data?.data?.nodes || []
    return currentNodes.filter((node: any) => node.user_level === formData.user_level).length
  }

  const regionStats = getRegionStats()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            {group ? '编辑EdgeTunnel服务组' : '创建EdgeTunnel服务组'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 基本信息 */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Server className="h-4 w-4" />
                基本信息
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-gray-700">服务名称 *</label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="例: 美国西部节点组"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="max_users" className="text-sm font-medium text-gray-700">最大用户数</label>
                  <Input
                    id="max_users"
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.max_users}
                    onChange={(e) => handleInputChange('max_users', parseInt(e.target.value) || 100)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium text-gray-700">描述</label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="服务组描述信息"
                />
              </div>
            </div>

            {/* API配置 */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Key className="h-4 w-4" />
                API配置
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="api_endpoint" className="text-sm font-medium text-gray-700">API地址 *</label>
                  <Input
                    id="api_endpoint"
                    value={formData.api_endpoint}
                    onChange={(e) => handleInputChange('api_endpoint', e.target.value)}
                    placeholder="https://your-edgetunnel-api.com"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="api_key" className="text-sm font-medium text-gray-700">API密钥 *</label>
                  <Input
                    id="api_key"
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => handleInputChange('api_key', e.target.value)}
                    placeholder="EdgeTunnel API密钥"
                    required
                  />
                </div>
              </div>
            </div>

            {/* IP节点配置 */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Globe className="h-4 w-4" />
                  {group ? '管理节点' : '节点配置'}
                </div>
                {group && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      总节点: {groupNodesData?.data?.data?.data?.nodes?.length || 0}
                    </Badge>
                    <Badge variant="outline" className={`text-xs ${getUserLevelColor(formData.user_level)}`}>
                      当前等级: {getCurrentLevelNodeCount()}
                    </Badge>
                  </div>
                )}
              </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="user_level" className="text-sm font-medium text-gray-700">节点等级</label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map(level => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => handleInputChange('user_level', level)}
                          className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                            formData.user_level === level 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {getUserLevelText(level)}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      选择用户等级决定哪些用户可以使用这些节点。切换等级将显示对应的节点IP
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="ip_list" className="text-sm font-medium text-gray-700">
                        {group ? `${getUserLevelText(formData.user_level)}节点IP` : '节点IP列表'}
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const sampleData = `172.64.229.168:2083#JP 官方优选 77ms
172.64.229.70:2083#JP 官方优选 77ms
104.18.152.206:2083#US 官方优选 163ms
104.16.246.31:2083#US 官方优选 167ms
104.27.85.90:2083#DE 官方优选 181ms
172.67.151.4:2083#NL 官方优选 204ms`
                            handleInputChange('ip_list', sampleData)
                          }}
                        >
                          示例格式
                        </Button>
                        {parsedIPs.length > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={removeDuplicateIPs}
                          >
                            <Filter className="h-3 w-3 mr-1" />
                            去重
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleInputChange('ip_list', '')}
                        >
                          清空
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* IP输入区域 */}
                      <div className="space-y-2">
                        <TextArea
                          id="ip_list"
                          value={formData.ip_list}
                          onChange={(e) => handleInputChange('ip_list', e.target.value)}
                          placeholder={`支持多种格式，例如：
简单格式: 1.2.3.4
带端口: 1.2.3.4:2083  
完整格式: 172.64.229.168:2083#JP 官方优选 77ms
批量粘贴: 支持从优选IP工具直接粘贴`}
                          rows={10}
                          className="font-mono text-sm"
                        />
                        <div className="text-xs text-gray-500 space-y-1">
                          <p>💡 <strong>智能解析支持：</strong></p>
                          <div className="ml-4 space-y-1">
                            <p>• <code>IP:端口#地区 描述 延迟</code> - 完整格式，自动提取地区和延迟</p>
                            <p>• <code>IP:端口#地区</code> - 带地区标识</p>
                            <p>• <code>IP:端口</code> - 带端口格式</p>
                            <p>• <code>IP</code> - 简单IP格式，使用默认端口2083</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* 解析预览区域 */}
                      {parsedIPs.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">智能解析结果</span>
                            <Badge variant="outline" className="text-xs">
                              {parsedIPs.length} 个节点
                            </Badge>
                          </div>
                          
                          {/* 地区统计 */}
                          {Object.keys(regionStats).length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {Object.entries(regionStats).map(([region, count]) => (
                                <Badge key={region} variant="outline" className="text-xs">
                                  {region}: {count}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          <div className="p-3 bg-gray-50 rounded-lg max-h-64 overflow-y-auto">
                            <div className="space-y-1 text-xs">
                              {parsedIPs.slice(0, 20).map((ip, index) => (
                                <div key={index} className="flex items-center gap-2 text-gray-600">
                                  <Globe className="h-3 w-3 flex-shrink-0" />
                                  <span className="font-mono">{ip.ip}:{ip.port}</span>
                                  {ip.region && (
                                    <Badge variant="outline" className="text-xs px-1 py-0">
                                      {ip.region}
                                    </Badge>
                                  )}
                                  {ip.latency && (
                                    <span className="text-green-600">{ip.latency}</span>
                                  )}
                                  {ip.description && (
                                    <span className="text-gray-500 truncate">{ip.description}</span>
                                  )}
                                </div>
                              ))}
                              {parsedIPs.length > 20 && (
                                <div className="text-gray-500 text-center py-2">
                                  ... 还有 {parsedIPs.length - 20} 个节点
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-blue-600">
                            将{group ? '添加/更新' : '创建'} {parsedIPs.length} 个{getUserLevelText(formData.user_level)}节点
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            {/* 操作按钮 */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onCancel}>
                取消
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending || addNodesMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending || addNodesMutation.isPending 
                  ? '处理中...' 
                  : (group ? (formData.ip_list.trim() ? '更新并添加节点' : '更新') : '创建')
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}